import { useId } from 'react'
import { useCountUp } from '../kit'
import { useArrived } from './Tooltip'

// The health ring: a score out of 100 drawn as a 14px stroke round a 148px
// circle, its colour the word's (ok at 75 and above, warning from 50, critical
// below), the number counting up in the middle as "63/100" with the word under
// it. The suffix is a prop because the same ring draws a percentage on the
// programme report, where "/100" would read as a score.

const R = 60
const C = 2 * Math.PI * R

export function ringColor(score: number): string {
  if (score >= 75) return 'var(--ok-solid)'
  if (score >= 50) return 'var(--warn-solid)'
  return 'var(--crit-solid)'
}

export function Ring({
  score,
  word,
  label,
  suffix = '/100',
}: {
  score: number
  word: string
  label?: string
  suffix?: string
}) {
  const arrived = useArrived()
  const shown = useCountUp(score, 800)
  const clamped = Math.max(0, Math.min(100, score))
  const color = ringColor(score)
  const grad = 'hc-ring-' + useId().replace(/:/g, '')
  return (
    <div className="hc-ring" role="img" aria-label={label ?? 'Health score ' + score + ' out of 100, ' + word}>
      <svg viewBox="0 0 148 148" aria-hidden="true">
        {/* The arc is lit from above: the word's colour, lighter where the
            stroke starts. The SVG turns -90deg, so x1 to x2 runs top to bottom. */}
        <defs>
          <linearGradient id={grad} x1="1" y1="0" x2="0" y2="0">
            <stop offset="0" style={{ stopColor: 'color-mix(in srgb, ' + color + ' 72%, white)' }} />
            <stop offset="1" style={{ stopColor: color }} />
          </linearGradient>
        </defs>
        <circle className="track" cx="74" cy="74" r={R} />
        <circle
          className="fill"
          cx="74"
          cy="74"
          r={R}
          strokeDasharray={C}
          style={{ strokeDashoffset: arrived ? C * (1 - clamped / 100) : C, stroke: 'url(#' + grad + ')' }}
        />
      </svg>
      <div className="val" aria-hidden="true">
        <div>
          <b>
            {shown}
            <small>{suffix}</small>
          </b>
          <span>{word}</span>
        </div>
      </div>
    </div>
  )
}
